import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchHouseholds } from '../api/householdsApi';
import { fetchVoiceListeners } from '../api/listenersApi';
import { fetchVoicesApi } from '../api/voicesApi';
import { ApiError, isAbortError, toApiError } from '../api/http';
import { useApp } from './AppContext';
import {
  BedtimeContextModel,
  BedtimePair,
  PairBookendState,
  PairBookends,
  deriveBedtimeContext,
  pairKey,
  replacePair,
} from './bedtime/bedtimeContextModel';
import { clearPrivateOfflineData, isCurrentSession, readOfflineSnapshot, removeOfflineFamily, saveOfflineSnapshot, sessionIdentity } from '../api/offlineSession';
import { reconcileOfflineCacheEpochs } from '../api/cacheEpochApi';
import { readBedtimeContext, writeBedtimeContext } from './bedtime/bedtimeContextStorage';

interface BedtimeContextValue extends BedtimeContextModel {
  refresh: () => Promise<void>;
  setPair: (pair: BedtimePair) => void;
  getPairBookends: (voiceId: string, listenerId: string) => PairBookendState | undefined;
  openPairDialog: (seed?: Partial<BedtimePair>) => void;
  closePairDialog: () => void;
  pairDialogOpen: boolean;
  pairDialogSeed: Partial<BedtimePair> | null;
  clearNotice: () => void;
}

const BedtimeContext = React.createContext<BedtimeContextValue | null>(null);

const emptyModel: BedtimeContextModel = {
  status: 'loading', pair: null, households: [], listeners: [], voices: [],
  missingBookends: [], notice: null, error: null,
};

export const BedtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state: appState, dispatch } = useApp();
  const userId = appState.user?.id || '';
  const [model, setModel] = useState<BedtimeContextModel>(emptyModel);
  const [pairBookends, setPairBookends] = useState<PairBookends>({});
  const [pairDialogOpen, setPairDialogOpen] = useState(false);
  const [pairDialogSeed, setPairDialogSeed] = useState<Partial<BedtimePair> | null>(null);
  const requestRef = useRef(0);
  const requestControllerRef = useRef<AbortController | null>(null);

  type FamilySnapshot = { households: BedtimeContextModel['households']; voices: BedtimeContextModel['voices']; listeners: BedtimeContextModel['listeners']; pairBookends: PairBookends };

  const refresh = useCallback(async () => {
    if (!userId) return;
    const requestId = ++requestRef.current;
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setModel((previous) => ({ ...previous, status: 'loading', error: null }));
    const identity = sessionIdentity();
    const restore = async () => {
      const saved = await readOfflineSnapshot<FamilySnapshot>('family', identity);
      if (requestId !== requestRef.current || controller.signal.aborted) return false;
      if (!saved || !Array.isArray(saved.households) || !Array.isArray(saved.voices) || !Array.isArray(saved.listeners) || !saved.pairBookends) return false;
      let next: BedtimeContextModel;
      try { next = deriveBedtimeContext({ ...saved, stored: readBedtimeContext(userId) }); } catch { return false; }
      setPairBookends(saved.pairBookends);
      dispatch({ type: 'SET_VOICES', payload: saved.voices });
      setModel({ ...next, offline: true });
      return true;
    };
    try {
      if (appState.offlineSession) {
        if (!await restore()) setModel({ ...emptyModel, status: 'error', error: new ApiError('offline') });
        return;
      }
      await reconcileOfflineCacheEpochs();
      if (requestId !== requestRef.current || controller.signal.aborted) return;
      const [households, voices] = await Promise.all([fetchHouseholds(controller.signal), fetchVoicesApi(controller.signal)]);
      if (requestId !== requestRef.current) return;
      dispatch({ type: 'SET_VOICES', payload: voices });

      const activeHouseholdIds = new Set(
        households.filter((household) => household.state === 'active').map((household) => household.household_id)
      );
      const readyVoices = voices.filter((voice) => (
        voice.status === 'ready'
        && voice.consent_active === true
        && typeof voice.household_id === 'string'
        && activeHouseholdIds.has(voice.household_id)
      ));
      const listenerResults = await Promise.allSettled(
        readyVoices.map(async (voice) => {
          return [voice.voice_id, await fetchVoiceListeners(voice.voice_id, controller.signal)] as const;
        })
      );
      if (requestId !== requestRef.current) return;

      const books: PairBookends = {};
      const failedCoverageVoiceIds = new Set<string>();
      const listenerResponses: Array<readonly [string, Awaited<ReturnType<typeof fetchVoiceListeners>>]> = [];
      listenerResults.forEach((result, index) => {
        if (result.status === 'fulfilled') listenerResponses.push(result.value);
        else if (isAbortError(result.reason)) throw result.reason;
        else failedCoverageVoiceIds.add(readyVoices[index].voice_id);
      });
      listenerResponses.forEach(([voiceId, listeners]) => {
        listeners.forEach((listener) => {
          books[pairKey(voiceId, listener.listener_id)] = {
            intro: Boolean(listener.bookends?.intro),
            goodnight: Boolean(listener.bookends?.goodnight),
          };
        });
      });
      // Household responses are the identity source. The per-voice endpoint
      // supplies only bookend coverage; merge it without trusting display text.
      const listeners = households.flatMap((household) => household.listeners.map((listener) => ({
        listener_id: listener.listener_id,
        household_id: household.household_id,
        display_name: listener.display_name,
        age_band: listener.age_band,
        state: listener.state,
        bookend_complete: Array.from(readyVoices).some((voice) => books[pairKey(voice.voice_id, listener.listener_id)]?.intro && books[pairKey(voice.voice_id, listener.listener_id)]?.goodnight),
      })));
      setPairBookends(books);
      const stored = readBedtimeContext(userId);
      const next = deriveBedtimeContext({
        households,
        voices,
        listeners,
        pairBookends: books,
        stored,
      });
      if (next.pair && failedCoverageVoiceIds.has(next.pair.voiceId)) {
        removeOfflineFamily(userId);
        setModel({
          ...next,
          status: 'error',
          error: new ApiError('service_unavailable', 'We couldn’t check personal messages for this voice.'),
        });
        return;
      }
      if (next.pair && (
        stored?.householdId !== next.pair.householdId
        || stored?.listenerId !== next.pair.listenerId
        || stored?.voiceId !== next.pair.voiceId
      )) {
        writeBedtimeContext(userId, next.pair);
      }
      if (identity && isCurrentSession(identity) && requestId === requestRef.current) {
        await saveOfflineSnapshot('family', { households, voices, listeners, pairBookends: books }, identity);
      }
      if (requestId === requestRef.current && !controller.signal.aborted) setModel(next);
    } catch (error) {
      if (isAbortError(error) || requestId !== requestRef.current) return;
      const apiError = toApiError(error);
      if (apiError.code === 'unauthorized') await clearPrivateOfflineData();
      else if (apiError.status === null && await restore()) return;
      setModel((previous) => ({ ...previous, status: 'error', error: toApiError(error) }));
    }
  }, [appState.offlineSession, dispatch, userId]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => () => requestControllerRef.current?.abort(), []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!userId || event.key !== `luma:bedtime-context:v1:${userId}`) return;
      void refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh, userId]);

  const setPair = useCallback((pair: BedtimePair) => {
    if (!userId) return;
    // Validate the complete draft before persisting it. A rejected draft must
    // leave both the committed page context and the durable record untouched.
    const candidate = replacePair(model, pair);
    if (!candidate.pair
      || candidate.pair.householdId !== pair.householdId
      || candidate.pair.listenerId !== pair.listenerId
      || candidate.pair.voiceId !== pair.voiceId) return;
    const stored = writeBedtimeContext(userId, pair);
    const next = deriveBedtimeContext({
      households: model.households,
      voices: model.voices,
      listeners: model.listeners,
      pairBookends,
      stored,
    });
    const listener = model.listeners.find((item) => item.listener_id === pair.listenerId);
    const voice = model.voices.find((item) => item.voice_id === pair.voiceId);
    setModel({
      ...next,
      offline: model.offline,
      notice: `Bedtime changed to ${listener?.display_name || 'your listener'} with ${voice?.voice_name || 'your Family voice'}.`,
    });
    setPairDialogSeed(null);
    setPairDialogOpen(false);
  }, [model, pairBookends, userId]);

  const getPairBookends = useCallback((voiceId: string, listenerId: string) => pairBookends[pairKey(voiceId, listenerId)], [pairBookends]);

  const clearNotice = useCallback(() => setModel((previous) => ({ ...previous, notice: null })), []);
  const openPairDialog = useCallback((seed?: Partial<BedtimePair>) => {
    setPairDialogSeed(seed || null);
    setPairDialogOpen(true);
  }, []);
  const closePairDialog = useCallback(() => {
    setPairDialogSeed(null);
    setPairDialogOpen(false);
  }, []);

  const value = useMemo<BedtimeContextValue>(() => ({
    ...model,
    refresh,
    setPair,
    getPairBookends,
    openPairDialog,
    closePairDialog,
    pairDialogOpen,
    pairDialogSeed,
    clearNotice,
  }), [clearNotice, closePairDialog, getPairBookends, model, openPairDialog, pairDialogOpen, pairDialogSeed, refresh, setPair]);

  return <BedtimeContext.Provider value={value}>{children}</BedtimeContext.Provider>;
};

export const useBedtimeContext = (): BedtimeContextValue => {
  const context = React.useContext(BedtimeContext);
  if (context) return context;
  // Unit-level pages may be rendered with the legacy AppContext provider only.
  // Keep the hook safe while production routes always use BedtimeProvider.
  return {
    ...emptyModel,
    refresh: async () => undefined,
    setPair: () => undefined,
    getPairBookends: () => undefined,
    openPairDialog: () => undefined,
    closePairDialog: () => undefined,
    pairDialogOpen: false,
    pairDialogSeed: null,
    clearNotice: () => undefined,
  };
};

export type { BedtimeContextValue };
